/**
 * <s-morph-confirm> — liquid "split" confirm for removing cart items.
 * Tapping a cart item's trash button morphs it into two joined bubbles,
 * "Remove" (red) and "Keep", that pull apart with a gooey neck while their
 * labels blur into focus (inspired by SwiftUI morphing buttons).
 * Remove runs the original remove command; Keep, Escape, tapping elsewhere
 * or 5 seconds of no action merge them back into the trash button.
 * Motion uses transform, opacity and a small blur only. Reduced motion: no morph.
 */
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const EASE_IN = 'cubic-bezier(0.4, 0, 0.7, 0.2)';

class SMorphConfirm extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.#addGooFilter();
    window.addEventListener('command', this.#onCommand, true);
  }

  disconnectedCallback() {
    window.removeEventListener('command', this.#onCommand, true);
    this.ready = false;
  }

  #addGooFilter() {
    if (document.getElementById('morph-goo')) return;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.style.position = 'absolute';
    svg.innerHTML = '<defs><filter id="morph-goo"><feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur"/><feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8" result="goo"/><feComposite in="SourceGraphic" in2="goo" operator="atop"/></filter></defs>';
    document.body.append(svg);
  }

  #onCommand = (event) => {
    if (event.command !== '--remove') return;
    const trigger = event.source;
    if (!(trigger instanceof HTMLElement) || !trigger.dataset.lineKey) return;
    if (trigger.dataset.morphConfirmed) { delete trigger.dataset.morphConfirmed; return; }
    if (trigger.closest('[data-morph-open]')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#open(trigger);
  };

  #open(trigger) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wrap = document.createElement('span');
    wrap.className = 'morph-confirm';
    wrap.dataset.morphOpen = '';

    const goo = document.createElement('span');
    goo.className = 'morph-confirm__goo';
    goo.setAttribute('aria-hidden', 'true');
    const blobA = document.createElement('span');
    const blobB = document.createElement('span');
    blobA.className = blobB.className = 'morph-confirm__blob';
    goo.append(blobA, blobB);

    const labels = document.createElement('span');
    labels.className = 'morph-confirm__labels';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'morph-confirm__btn morph-confirm__btn--remove';
    remove.textContent = this.dataset.removeLabel || 'Remove';
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'morph-confirm__btn';
    keep.textContent = this.dataset.keepLabel || 'Keep';
    labels.append(remove, keep);
    wrap.append(goo, labels);

    trigger.style.display = 'none';
    trigger.after(wrap);

    // Size the blobs to the labels.
    const sizeBlobs = () => {
      blobA.style.width = `${remove.offsetWidth}px`;
      blobB.style.width = `${keep.offsetWidth}px`;
      blobB.style.left = `${keep.offsetLeft}px`;
    };
    sizeBlobs();

    let closed = false;
    const close = (confirmed) => {
      if (closed) return;
      closed = true;
      clearTimeout(timer);
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', onKey, true);
      const finish = () => {
        wrap.remove();
        trigger.style.display = '';
        if (confirmed) {
          trigger.dataset.morphConfirmed = '1';
          trigger.click();
        } else {
          trigger.focus({ preventScroll: true });
        }
      };
      if (reduce) { finish(); return; }
      const shift = -keep.offsetLeft;
      const opts = { duration: 260, easing: EASE_IN, fill: 'forwards' };
      labels.animate([{ opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(6px)' }], { duration: 160, easing: 'ease-in', fill: 'forwards' });
      blobB.animate([{ transform: 'translateX(0) scale(1)' }, { transform: `translateX(${shift}px) scale(0.6)` }], opts);
      blobA.animate([{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(0.4)', opacity: 0 }], opts).finished.then(finish, finish);
    };

    const outside = (e) => { if (!wrap.contains(e.target)) close(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(false); } };
    remove.addEventListener('click', () => close(true));
    keep.addEventListener('click', () => close(false));
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', onKey, true);
    const timer = setTimeout(() => close(false), 5000);

    remove.focus({ preventScroll: true });
    if (reduce) return;

    // Split: both bubbles start as one at the trash button, then pull apart with a gooey neck.
    const shift = -keep.offsetLeft;
    blobA.animate([{ transform: 'scale(0.45)' }, { transform: 'scale(1)' }], { duration: 520, easing: SPRING });
    blobB.animate(
      [{ transform: `translateX(${shift}px) scale(0.6)` }, { transform: `translateX(${shift * 0.15}px) scale(0.92)`, offset: 0.35 }, { transform: 'translateX(0) scale(1)' }],
      { duration: 640, easing: SPRING }
    );
    labels.animate(
      [{ opacity: 0, filter: 'blur(8px)', transform: 'scale(0.92)' }, { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }],
      { duration: 420, delay: 90, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'backwards' }
    );
  }
}

if (!customElements.get('s-morph-confirm')) customElements.define('s-morph-confirm', SMorphConfirm);
